import { useState } from 'react';
import PropTypes from 'prop-types';

import { compileRuleNarrative } from './ruleNarrative';

/**
 * Read-only one-rule view: a BD-friendly narrative one-liner (collapsed)
 * with an optional expand panel that shows the actual fields/constraints
 * in that rule's IF / THEN / ELSE — but ONLY the fields present, never the
 * full chip-list of unchecked schema fields.
 */
export default function RuleSummary({ rule, ruleIndex }) {
	const [expanded, setExpanded] = useState(false);
	const narrative = compileRuleNarrative(rule);

	return (
		<div
			className="dsv-view__rule-summary"
			role="group"
			aria-label={`Conditional rule ${ruleIndex + 1}`}
			data-testid={`dsv-view-xallof-rule-${ruleIndex}`}
			style={cardStyle}
		>
			<button
				type="button"
				onClick={() => setExpanded((e) => !e)}
				aria-expanded={expanded}
				data-testid={`dsv-view-xallof-rule-${ruleIndex}-toggle`}
				style={toggleStyle}
			>
				<span style={chevronStyle} aria-hidden="true">{expanded ? '▾' : '▸'}</span>
				<span style={ruleIndexStyle}>Rule {ruleIndex + 1}</span>
				<span
					style={narrativeStyle}
					data-testid={`dsv-view-xallof-rule-${ruleIndex}-narrative`}
				>
					{renderNarrativeSentence(narrative)}
				</span>
			</button>

			{expanded && (
				<div
					className="dsv-view__rule-detail"
					style={detailStyle}
					data-testid={`dsv-view-xallof-rule-${ruleIndex}-detail`}
				>
					<BranchBlock
						kind="if"
						label="IF"
						accent="#4F5AED"
						items={(rule.ifConditions || []).filter((c) => c && c.field)}
						renderItem={(c) => (
							<ConditionRow condition={c} />
						)}
					/>
					<BranchBlock
						kind="then"
						label="THEN"
						accent="#16a34a"
						items={(rule.thenFields || []).filter((f) => f && f.fieldName)}
						renderItem={(f) => (
							<BranchFieldRow field={f} />
						)}
						emptyMessage="No fields constrained when the IF matches."
					/>
					{Array.isArray(rule.elseFields) && rule.elseFields.length > 0 && (
						<BranchBlock
							kind="else"
							label="ELSE"
							accent="#ea580c"
							items={rule.elseFields.filter((f) => f && f.fieldName)}
							renderItem={(f) => (
								<BranchFieldRow field={f} />
							)}
						/>
					)}
				</div>
			)}
		</div>
	);
}

RuleSummary.propTypes = {
	rule: PropTypes.object.isRequired,
	ruleIndex: PropTypes.number.isRequired,
};

function renderNarrativeSentence(narrative) {
	const parts = [narrative.condition];
	if (narrative.then) {
		if (narrative.then.kind === 'single') {
			parts.push(`then ${narrative.then.text}`);
		} else {
			parts.push(`then ${narrative.then.headline} ${narrative.then.fields.slice(0, 3).join(', ')}${narrative.then.fields.length > 3 ? ', …' : ''}`);
		}
	}
	if (narrative.elsePart) {
		if (narrative.elsePart.kind === 'single') {
			parts.push(`else ${narrative.elsePart.text}`);
		} else {
			parts.push(`else ${narrative.elsePart.headline} ${narrative.elsePart.fields.slice(0, 3).join(', ')}${narrative.elsePart.fields.length > 3 ? ', …' : ''}`);
		}
	}
	return parts.join(' ');
}

function BranchBlock({ label, accent, items, renderItem, emptyMessage }) {
	return (
		<section
			style={{
				borderLeft: `3px solid ${accent}`,
				background: '#f8fafc',
				padding: '8px 12px',
				margin: '6px 0',
				borderRadius: '0 4px 4px 0',
			}}
		>
			<header style={branchHeaderStyle}>{label}</header>
			{items.length === 0 ? (
				<p style={emptyStyle}>{emptyMessage || `No ${label.toLowerCase()} entries.`}</p>
			) : (
				<ul style={listStyle}>
					{items.map((item, i) => (
						<li key={i} style={listItemStyle}>
							{renderItem(item)}
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

BranchBlock.propTypes = {
	label: PropTypes.string.isRequired,
	accent: PropTypes.string.isRequired,
	items: PropTypes.array.isRequired,
	renderItem: PropTypes.func.isRequired,
	emptyMessage: PropTypes.string,
};

function ConditionRow({ condition }) {
	const { field, operator, value } = condition;
	return (
		<>
			<strong>{field}</strong>
			<span style={{ color: '#6b7280' }}> {operatorText(operator)}</span>
			{operator !== 'type_is_null' && (
				<span style={valueStyle}> {formatValueForDisplay(value)}</span>
			)}
		</>
	);
}

ConditionRow.propTypes = { condition: PropTypes.object.isRequired };

function BranchFieldRow({ field }) {
	return (
		<>
			<strong>{field.fieldName}</strong>
			<span style={badgeStyle(field.required ? '#dbeafe' : '#f3f4f6', field.required ? '#1e40af' : '#4b5563')}>
				{field.required ? 'required' : 'optional'}
			</span>
			{field.type && (
				<span style={{ color: '#6b7280' }}> · type: {field.type}</span>
			)}
			{field.nullableInBranch && (
				<span style={{ color: '#6b7280' }}> · nullable</span>
			)}
			<ConstraintLine field={field} />
		</>
	);
}

BranchFieldRow.propTypes = { field: PropTypes.object.isRequired };

function ConstraintLine({ field }) {
	const parts = [];
	if (field.minLength != null) parts.push(`minLen ${field.minLength}`);
	if (field.maxLength != null) parts.push(`maxLen ${field.maxLength}`);
	if (field.minimum != null) parts.push(`min ${field.minimum}`);
	if (field.maximum != null) parts.push(`max ${field.maximum}`);
	if (field.exclusiveMinimum != null) parts.push(`> ${field.exclusiveMinimum}`);
	if (field.exclusiveMaximum != null) parts.push(`< ${field.exclusiveMaximum}`);
	if (field.pattern) parts.push(`pattern ${field.pattern}`);
	if (field.format) parts.push(`format ${field.format}`);
	if (Array.isArray(field.enumValues) && field.enumValues.length > 0) {
		parts.push(`enum: ${field.enumValues.join(', ')}`);
	}
	if (parts.length === 0) return null;
	return <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{parts.join(' · ')}</div>;
}

ConstraintLine.propTypes = { field: PropTypes.object.isRequired };

function operatorText(operator) {
	const map = {
		const: 'equals',
		enum: 'is one of',
		not_in: 'is not one of',
		minimum: '≥',
		maximum: '≤',
		exclusive_minimum: '>',
		exclusive_maximum: '<',
		contains_const: 'contains',
		type_is_null: 'is empty',
	};
	return map[operator] || operator;
}

function formatValueForDisplay(value) {
	if (Array.isArray(value)) return value.map(String).join(', ');
	if (value == null) return '';
	return String(value);
}

const cardStyle = {
	border: '1px solid #e5e7eb',
	borderRadius: '6px',
	margin: '8px 0',
	background: '#fff',
};
const toggleStyle = {
	width: '100%',
	textAlign: 'left',
	background: 'none',
	border: 'none',
	cursor: 'pointer',
	padding: '10px 14px',
	display: 'flex',
	alignItems: 'baseline',
	gap: '10px',
	fontSize: '14px',
	lineHeight: 1.5,
};
const chevronStyle = { color: '#6b7280', fontSize: '13px', flex: '0 0 auto' };
const ruleIndexStyle = { fontWeight: 600, color: '#374151', flex: '0 0 auto' };
const narrativeStyle = { color: '#111827', flex: '1 1 auto', wordBreak: 'break-word' };
const detailStyle = { padding: '0 14px 12px 14px' };
const branchHeaderStyle = {
	fontSize: '11px',
	fontWeight: 700,
	letterSpacing: '0.04em',
	color: '#374151',
	marginBottom: '6px',
};
const listStyle = { margin: 0, paddingLeft: '14px' };
const listItemStyle = { fontSize: '13px', lineHeight: 1.6 };
const emptyStyle = { fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', margin: 0 };
const valueStyle = { color: '#111827', fontWeight: 500 };

function badgeStyle(bg, fg) {
	return {
		display: 'inline-block',
		marginLeft: '6px',
		padding: '0 6px',
		background: bg,
		color: fg,
		borderRadius: '3px',
		fontSize: '10px',
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.02em',
	};
}
